import z from "zod";
import { createStore, useStore } from "zustand";
import { createJSONStorage, devtools, persist } from "zustand/middleware";
import { PAGE_SIZE_OPTIONS } from "../constant";

export const orderDirectionSchema = z.enum(["asc", "desc"]).nullish();
export type OrderDirection = z.infer<typeof orderDirectionSchema>;
export const orderBySchema = z.string().nullish();
export type OrderBy = z.infer<typeof orderBySchema>;

export type TableState = {
  offset: number;
  limit: number;
  orderBy: OrderBy;
  orderDirection: OrderDirection;
};

export interface TableOrderState {
  orderBy: OrderBy;
  orderDirection: OrderDirection;
}

export const tableSchema = z.object({
  offset: z.number(),
  limit: z.number(),
  order_by: orderBySchema,
  order_direction: orderDirectionSchema,
});

export const tableResponseSchema = <T extends z.ZodTypeAny>(itemSchema: T) =>
  z.object({
    items: z.array(itemSchema),
    total: z.number(),
    offset: z.number(),
    limit: z.number(),
  });

export type TableActions = {
  setOffset: (offset: number) => void;
  setLimit: (limit: number) => void;
  toggleOrder: (columnId: string | null) => void;
  setPagination: (newOffset: number, newLimit: number) => void;
};

/** 정렬 3단 순환: 미정렬 → asc → desc → 미정렬. 목록과 이력 모달이 함께 쓴다 */
export const nextOrderDirection = (
  isSameColumn: boolean,
  current: OrderDirection,
): OrderDirection => {
  if (!isSameColumn) return "asc";
  if (current === "asc") return "desc";
  if (current === "desc") return null;
  return "asc";
};

export const createTableStore = (
  storeName: string,
  defaultLimit: number = PAGE_SIZE_OPTIONS[0],
) =>
  createStore<TableState & TableActions>()(
    devtools(
      persist(
        (set) => ({
          offset: 0,
          limit: defaultLimit,
          setOffset: (offset: number) => set({ offset }),
          setLimit: (limit: number) => set({ limit }),
          orderBy: null,
          orderDirection: null,
          toggleOrder: (columnId: string | null) =>
            set((state) => {
              const nextDirection = nextOrderDirection(
                state.orderBy === columnId,
                state.orderDirection,
              );

              return {
                orderBy: nextDirection && columnId,
                orderDirection: nextDirection,
                // 정렬 기준이 바뀌면 첫 페이지부터 본다 — 3페이지에 머문 채 순서가
                // 바뀌면 사용자가 방금 정렬한 상위 항목이 화면에 없다
                offset: 0,
              };
            }),
          setPagination: (newOffset: number, newLimit: number) =>
            set({
              offset: newOffset,
              limit: newLimit,
            }),
        }),
        {
          name: `${storeName}-tableStore`,
          storage: createJSONStorage(() => sessionStorage),
        },
      ),
    ),
  );

export type FilterState<T> = {
  filterValues: T;
};

export type FilterActions<T> = {
  setFilter: <K extends keyof T>(key: K, value: T[K]) => void;
  resetFilters: () => void;
};

/**
 * 필터 스토어의 최소 계약. 스토어의 필터 타입을 모르는 공용 코드(딥링크 훅, 필터
 * 레이아웃, 목록 페이지 쉘)가 이것만 받는다.
 */
export interface FilterStoreSlice<T extends Record<string, unknown>> {
  filterValues: T;
  setParams: <K extends keyof T>(key: K, value: T[K]) => void;
  resetFilters: () => void;
}

/**
 * 문자열 키로 부르는 setParams.
 *
 * `setParams` 는 `keyof T` 만 받으므로 문자열 키를 쓰는 공용 코드에 그대로 넘기지
 * 못한다. 넓히는 캐스팅은 이 타입을 쓰는 공용 모듈 안에서 한 번만 하고, 호출부에는
 * 두지 않는다 — 페이지·필터 26곳에 흩어져 있던 캐스팅이 그렇게 생겼다.
 * 실제 키·값의 정합성은 스토어 뒤의 request 스키마 `.parse()` 가 다시 확인한다.
 */
export type LooseSetParams = (key: string, value: unknown) => void;

export const asLooseSetParams = <T extends Record<string, unknown>>(
  setParams: FilterStoreSlice<T>["setParams"],
): LooseSetParams => setParams as LooseSetParams;

const createFilterStore = <T extends Record<string, unknown>>(
  storeName: string,
  defaultValue: T,
) =>
  createStore<FilterState<T> & FilterActions<T>>()(
    devtools(
      persist(
        (set) => ({
          filterValues: defaultValue,
          setFilter: (key, value) =>
            set((state) => ({
              filterValues: { ...state.filterValues, [key]: value },
            })),
          resetFilters: () => set({ filterValues: defaultValue }),
        }),
        {
          name: `${storeName}-filterStore`,
          storage: createJSONStorage(() => sessionStorage),
          // 화면에서 사라진 필터 키는 버린다 — 남으면 UI 없이 계속 전송돼 결과가 조용히 좁혀진다
          merge: (persisted, current) => {
            const saved = (persisted as Partial<FilterState<T>> | undefined)
              ?.filterValues;
            if (!saved) return current;

            const known = Object.keys(defaultValue);
            return {
              ...current,
              filterValues: {
                ...defaultValue,
                ...Object.fromEntries(
                  Object.entries(saved).filter(([key]) => known.includes(key)),
                ),
              } as T,
            };
          },
        },
      ),
    ),
  );

export const createTablePageStore = <T extends Record<string, unknown>>(
  storeName: string,
  defaultValue: T = {} as T,
  defaultLimit?: number,
) => {
  const tableStore = createTableStore(storeName, defaultLimit);
  const filterStore = createFilterStore(storeName, defaultValue);

  function useCombinedStore() {
    const {
      offset,
      limit,
      orderBy,
      orderDirection,
      setOffset,
      setLimit,
      ...orderActions
    } = useStore(tableStore);

    const { filterValues, setFilter, resetFilters } = useStore(filterStore);

    const params = {
      ...filterValues,
      // search_type keyword가 모두 존재할 때만 포함
      ...(!!filterValues?.search_type && !!filterValues?.keyword
        ? {
            search_type: filterValues.search_type,
            keyword: filterValues.keyword,
          }
        : { search_type: undefined, keyword: undefined }),
      // date_type이 존재하고 start_date와 end_date가 모두 존재할 때만 포함
      ...(!!filterValues?.date_type &&
      filterValues?.start_date &&
      filterValues?.end_date
        ? {
            date_type: filterValues.date_type,
            start_date: filterValues.start_date,
            end_date: filterValues.end_date,
          }
        : {
            date_type: undefined,
            start_date: undefined,
            end_date: undefined,
          }),
      offset,
      limit,
      order_by: orderBy,
      order_direction: orderDirection,
    } as unknown as T &
      Omit<TableState, "orderBy" | "orderDirection"> & {
        order_by: OrderBy;
        order_direction: OrderDirection;
      };

    const setParams = <K extends keyof T>(key: K, value: T[K]) => {
      setFilter(key, value);
      setOffset(0);
    };

    // 필터 초기화도 조건 변경이므로 첫 페이지로 돌아간다.
    // filterStore 와 tableStore 가 분리돼 있어 여기서 묶어 준다
    const resetAllFilters = () => {
      resetFilters();
      setOffset(0);
    };

    return {
      offset,
      limit,
      setOffset,
      setLimit,
      orderBy,
      orderDirection,
      ...orderActions,
      filterValues,
      setFilter,
      resetFilters: resetAllFilters,
      params,
      setParams,
    };
  }

  return useCombinedStore;
};
